import type { OfferListEventHandler } from 'components/OfferList/OfferListEventsContext'
import OfferListEventsContext, {
  OfferListEvents,
  OfferListEventsProvider,
} from 'components/OfferList/OfferListEventsContext'
import type { ComponentProps } from 'react'
import { memo, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import OfferCardMapper from './OfferCardMapper'
import type { LEOfferPricingComponent } from './LEOfferPriceDetails'
import { EventDataKey } from 'home/pages/HomePage/useHomepageAnalytics'
import useOffer from 'hooks/Offers/useOffer'
import isHotelFlightBundleOffer from 'lib/offer/isHotelFlightBundleOffer'
import styled from 'styled-components'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { rem } from 'polished'
import clsx from 'clsx'
import OfferCardLoadingSkeleton from 'components/OfferList/OfferCards/OfferCardLoadingSkeleton'
import ErrorBoundary from 'components/Common/ErrorBoundary'
import { useInView } from 'react-intersection-observer'
import LoaderPlayStateContext from 'contexts/LoaderPlayStateContext'
import useToggle from 'hooks/useToggle'
import {
  calculateHasBeenInView,
  calculateTrackingInView,
  handleSkipInView,
} from 'components/utils/OfferInView'
import { OFFER_TRACKING_IN_VIEW_THRESHOLD } from 'constants/offerList'
import { REBRAND_PALETTE_CLASS } from 'lib/theme/paletteVersion'
import useGrowthOfferTile from './GrowthOfferCard/useGrowthOfferTile'

const Card = styled.div`
  display: grid;
  width: 80vw;
  height: 100%;
  flex-shrink: 0;

  // Sold out villas return null so need to be hidden
  &:empty {
    display: none;
  }

  > * {
    grid-column: 1 / -1;
    grid-row: 1 / -1;
  }

  ${mediaQueryUp.tablet} {
    width: ${rem(320)};
  }

  ${mediaQueryUp.desktop} {
    &.size-medium {
      width: ${rem(512)};
    }
  }

  &.compact-hotel-card,
  &.chat-cruise-carousel-card {
    width: 100%;
    min-width: 0;
    min-height: ${rem(346)};
    gap: ${rem(8)};

    > * {
      width: 100%;
      min-width: 0;
    }

    > * > :first-child {
      height: ${rem(158)};
      aspect-ratio: auto;
    }

    > * > :last-child {
      gap: ${rem(8)};
    }

    ${mediaQueryUp.desktop} {
      width: ${rem(320)};
      min-height: ${rem(300)};
      gap: ${rem(16)};

      > * > :first-child {
        height: auto;
        aspect-ratio: 16 / 9;
      }

      > * > :last-child {
        gap: ${rem(16)};
      }
    }
  }

  &.chat-experience-carousel-card {
    width: 100%;
    min-width: 0;
    min-height: ${rem(346)};
    gap: ${rem(8)};

    // ExperienceOfferCard wraps the card in an extra element that LEOfferCard omits,
    // so these styles must reach one level deeper than .compact-hotel-card.
    > *,
    > * > * {
      width: 100%;
      min-width: 0;
    }

    > * > * > :first-child {
      height: ${rem(158)};
      aspect-ratio: auto;
    }

    > * > * > :last-child {
      gap: ${rem(8)};
    }

    ${mediaQueryUp.desktop} {
      width: ${rem(320)};
      min-height: ${rem(300)};
      gap: ${rem(16)};

      > * > * > :first-child {
        height: auto;
        aspect-ratio: 16 / 9;
      }

      > * > * > :last-child {
        gap: ${rem(16)};
      }
    }
  }

  /* Fix width for hidden offer cards to prevent overlap on tablet and larger screens */
  &:has(.lux-plus-hidden-offer-card) {
    ${mediaQueryUp.tablet} {
      width: ${rem(512)};

      &.size-small {
        width: ${rem(320)};
      }
    }

    &.compact-hotel-card {
      width: 100%;

      ${mediaQueryUp.desktop} {
        width: ${rem(320)};
      }
    }
  }

  body.${REBRAND_PALETTE_CLASS} & {
    border-radius: var(--border-radius-m);
    overflow: hidden;
  }
`

const LoadingSkeleton = styled(OfferCardLoadingSkeleton)`
  z-index: 0;
  opacity: 0;
  transition: opacity 0.2s;
  pointer-events: none;

  &.visible {
    opacity: 1;
  }
`

type InheritedOfferCardMapperProps = Pick<ComponentProps<typeof OfferCardMapper>, 'variant' | 'size' | 'eagerLoadImage'>

interface Props extends InheritedOfferCardMapperProps {
  id: string
  idx?: number
  className?: string
  lazyLoad?: boolean
  preferListingOffer?: boolean
  additionalOfferUrlQuery?: string
  filters?: App.OfferListFilters
  selectedDatesAndRoomsFilter?: App.OfferListFilters
  listKey?: string
  distance?: number
  distanceFrom?: string
  PricingComponent?: LEOfferPricingComponent
  CtaComponent?: React.ComponentType
}

function OfferCard({
  id,
  idx,
  lazyLoad,
  preferListingOffer = false,
  additionalOfferUrlQuery,
  filters,
  variant,
  size,
  selectedDatesAndRoomsFilter,
  eagerLoadImage,
  listKey,
  distance,
  distanceFrom,
  className,
  PricingComponent,
  CtaComponent,
}: Props) {
  const dispatchOfferListEvent = useContext(OfferListEventsContext)
  const [skipInView, setSkipInView] = useState(false)
  const [imageLoaded, setImageLoaded] = useState(false)

  const [tileInViewRef, inView, entry] = useInView({
    rootMargin: '100px',
    threshold: [0, OFFER_TRACKING_IN_VIEW_THRESHOLD],
    skip: skipInView,
  })

  useEffect(() => {
    handleSkipInView(entry, skipInView, setSkipInView)
  }, [entry, skipInView, setSkipInView, inView])

  const hasBeenInView = useMemo(() => calculateHasBeenInView(entry), [entry])

  const trackingInView = useMemo(() => calculateTrackingInView(entry), [entry])

  const canFetchOffer = !lazyLoad || hasBeenInView

  const [offer, , offerError] = useOffer(id, {
    requireSummaryOnly: true,
    preferListingOffer,
    disabled: !canFetchOffer,
  })

  const isGrowthTile = useGrowthOfferTile()

  const [loader,,, hideLoader] = useToggle(!offer)

  useEffect(() => {
    if (offer && trackingInView && imageLoaded && idx !== undefined) {
      dispatchOfferListEvent({
        offer,
        position: idx,
        type: OfferListEvents.productImpression,
        key: EventDataKey.ProductImpression,
      })
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [trackingInView, offer, imageLoaded])

  const onListEvent = useCallback<OfferListEventHandler>((dispatchAction, additionalData) => {
    if (dispatchAction.type === OfferListEvents.offerReady) {
      setImageLoaded(true)
    }
    // Now that we've handled our case, propagate the event up to other listeners
    dispatchOfferListEvent(dispatchAction, additionalData)
  }, [dispatchOfferListEvent])

  if (offerError) {
    return null
  }

  // Points carousels must not mount an empty Card shell for Hotel+Flight bundles.
  // List filters can briefly include these IDs before campaign tags hydrate; drop the
  // whole tile here so force-bundle cash pricing never appears in the carousel.
  if (offer && filters?.viewPriceInPoints && isHotelFlightBundleOffer(offer)) {
    return null
  }

  const pauseLoader = !hasBeenInView || !!offer

  return (
    <ErrorBoundary fallback={null}>
      <LoaderPlayStateContext.Provider value={pauseLoader ? 'paused' : 'running'}>
        <Card ref={tileInViewRef} className={clsx(`size-${isGrowthTile ? 'small' : size}`, className)} data-testid="offer-card">

          {!!offer && <OfferListEventsProvider onListEvent={onListEvent}>
            <OfferCardMapper
              offer={offer}
              size={size}
              variant={variant}
              idx={idx}
              className={className}
              additionalOfferUrlParams={additionalOfferUrlQuery}
              filters={filters}
              selectedDatesAndRoomsFilter={selectedDatesAndRoomsFilter}
              eagerLoadImage={eagerLoadImage}
              listKey={listKey}
              distance={distance}
              distanceFrom={distanceFrom}
              PricingComponent={PricingComponent}
              CtaComponent={CtaComponent}
            />
          </OfferListEventsProvider>}
          {loader && <LoadingSkeleton
            growthTile={isGrowthTile}
            variant={variant}
            onTransitionEnd={hideLoader}
            className={clsx(className, !offer && 'visible')}
            size={isGrowthTile ? 'small' : size}
          />}
        </Card>
      </LoaderPlayStateContext.Provider>
    </ErrorBoundary>
  )
}

export default memo(OfferCard)
